import { useMemo } from 'react';

import { ColDef, GridOptions, ValueGetterParams } from 'ag-grid-community';
import { ClaimRow } from 'api/endpoints';
import { ExhibitTable } from 'components/ExhibitsExport/ExhibitTable';
import { SvgIcon } from 'components/UIComponents/SvgIcon';
import { ExhibitTemplates, getTemplateName } from '../../templates/exhibitType';
import { getClaimNumber } from '../../utils/field';

import { ApIconInfoTooltip } from 'components/UIComponents/ApIconTooltip';
import { formatMoney } from 'utils/formatNumber';
import './ClaimsTable.css';

const AMOUNT_ALLOWED_BY_COURT = 100;

interface Props {
  resolutionSubStatusId?: number;
  claims: ClaimRow[];
  loading?: boolean;
  setSelectedClaims: (claims: ClaimRow[]) => void;
}

export const ClaimsTable = ({
  resolutionSubStatusId,
  claims,
  loading,
  setSelectedClaims,
}: Props) => {
  const columnDefs = useMemo(
    () => getColumnDefs(getTemplateName(resolutionSubStatusId ?? 0)),
    [resolutionSubStatusId],
  );

  const showWarning = claims.length > AMOUNT_ALLOWED_BY_COURT;

  return (
    <ExhibitTable
      rows={claims}
      loading={loading}
      title={`${claims?.length} claims to be included in the exhibit`}
      warningMessage={showWarning ? <WarningMessage /> : undefined}
      columnDefs={columnDefs}
      onSelectedChange={setSelectedClaims}
      gridOptions={gridOptions}
    />
  );
};

const WarningMessage = () => (
  <div className="exhibit-contracts-amount-warning">
    <SvgIcon iconName="alert_amber" width={15} height={11} />
    The number on this exhibit may exceed the amount allowed by the court
  </div>
);

const EmptyScheduleNumber = () => (
  <span className="claim-schedule-number_novalue">
    <SvgIcon iconName="exclam_circle" width={18} height={16} fill="red" />
    no data
  </span>
);

const gridOptions: GridOptions<ClaimRow> = {
  rowClassRules: {
    'claims-table__row-error': ({ data }) => !getClaimNumber(data),
  },
};

const getColumnDefs = (template?: string): ColDef[] => {
  const debtorColumn: ColDef =
    template === ExhibitTemplates.ExpungeWithSurviving
      ? { field: 'currentDebtor', headerName: 'Current debtor' }
      : { field: 'proposedDebtor', headerName: 'Proposed debtor' };

  return [
    {
      field: 'active',
      headerName: '',
      headerCheckboxSelection: true,
      checkboxSelection: true,
      showDisabledCheckboxes: true,
      width: 10,
    },
    {
      // headerName: 'Claims number',
      headerComponent: () => (
        <span className="exhibit-contracts__grid-header__claims-number">
          Claims number
          <ApIconInfoTooltip
            message={'"Claim Schedule Number" will be used for all Schedule Claims'}
          />
        </span>
      ),
      width: 150,
      cellRenderer: (params: { value: string }) =>
        params.value ? `#${params.value}` : <EmptyScheduleNumber />,
      valueGetter: ({ data }: ValueGetterParams<ClaimRow>) => getClaimNumber(data),
    },
    {
      field: 'counterpartyName',
      headerName: 'Counterparty',
      flex: 3,
      cellClass: 'ag-exhibit-bold-font',
    },
    {
      ...debtorColumn,
      flex: 3,
    },
    {
      field: 'currentTotal',
      headerName: 'Current total',
      width: 150,
      type: 'rightAligned',
      cellClass: 'ag-right-aligned-cell',
      cellRenderer: (params: { value: number }) => formatMoney(params.value),
    },
  ];
};
